import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CalendarDays } from 'lucide-react';
import { trackEvent } from '../lib/analytics';
import { HERO_LQIP } from '../lib/hero-lqip';

export const Hero = ({ onPlanTrip }) => {
  const { t } = useLanguage();
  // Start with LQIP; swap to full URL when it has finished loading
  const [bgUrl, setBgUrl] = useState(HERO_LQIP);

  useEffect(() => {
    const webp = '/hero-golf-mallorca.webp';
    const jpg = '/hero-golf-mallorca.jpg';
    let cancelled = false;

    const tryLoad = (src, onSuccess, onFail) => {
      const i = new Image();
      i.onload = () => { if (!cancelled) onSuccess(src); };
      i.onerror = onFail;
      i.src = src;
    };

    tryLoad(webp,
      (src) => setBgUrl(src),
      () => tryLoad(jpg, (src) => setBgUrl(src), () => {})
    );

    return () => { cancelled = true; };
  }, []);

  const isFullLoaded = bgUrl !== HERO_LQIP;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBookTeeTime = () => {
    trackEvent('book_tee_time_click', { location: 'hero', destination: 'greenfee365_home' });
  };

  const handlePlanTrip = () => {
    trackEvent('trip_planner_open', { location: 'hero' });
    onPlanTrip();
  };

  return (
    <section id="hero" className="hero-section grain-overlay" data-testid="hero-section">
      {/* Single hero background — starts as blurred LQIP, swaps to full image when loaded */}
      <div
        className={`hero-bg ${isFullLoaded ? 'hero-bg-loaded' : 'hero-bg-lqip'}`}
        style={{ backgroundImage: `url('${bgUrl}')` }}
      />
      <div className="hero-overlay" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center px-4 sm:px-6 pt-40 sm:pt-44 md:pt-48 pb-24">
        <div className="container-custom w-full">
          <div className="max-w-3xl">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-5 sm:mb-6 opacity-0 animate-fade-in-up stagger-1"
              data-testid="hero-subtitle"
            >
              <span className="text-white font-semibold text-xs sm:text-sm tracking-wide">Est. 2003</span>
              <span className="w-px h-4 sm:h-5 bg-white/50"></span>
              <span className="text-white font-semibold text-xs sm:text-sm tracking-wide">Mallorca's First Golf Agency</span>
            </div>

            {/* Brand story */}
            <p
              className="text-white text-base sm:text-lg md:text-xl leading-relaxed mb-8 sm:mb-10 max-w-2xl opacity-0 animate-fade-in-up stagger-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)] font-medium"
              style={{ color: '#ffffff' }}
              data-testid="hero-brand-story"
            >
              Leaders in innovation with the island's first 24/7 real-time booking system service.
            </p>

            {/* Title */}
            <h1
              className="font-heading text-4xl sm:text-5xl md:text-7xl text-white font-medium leading-tight mb-8 sm:mb-10 opacity-0 animate-fade-in-up stagger-3 drop-shadow-[0_4px_8px_rgba(0,0,0,0.7)]"
              style={{ color: '#ffffff' }}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 opacity-0 animate-fade-in-up stagger-4">
              <a
                href="https://golfinmallorca.greenfee365.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleBookTeeTime}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-white text-stone-800 hover:bg-stone-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                data-testid="hero-book-tee-time"
              >
                {t('hero.bookTeeTime')}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
              <button
                onClick={handlePlanTrip}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-white/15 backdrop-blur-md text-white border border-white/30 hover:bg-white/25 transition-all duration-300 shadow-lg"
                data-testid="hero-plan-trip"
              >
                <CalendarDays className="w-4 h-4" />
                Trip Planner
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Hidden on mobile to avoid overlapping Trip Planner pill */}
      <button
        onClick={() => scrollToSection('about')}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 group hidden sm:flex"
        data-testid="scroll-indicator"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/60 text-xs uppercase tracking-[0.2em] font-light group-hover:text-white/80 transition-colors">Scroll</span>
          <div className="w-8 h-14 rounded-full border-2 border-white/30 flex items-start justify-center pt-2 group-hover:border-white/50 transition-colors">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce group-hover:bg-white/80 transition-colors"></div>
          </div>
        </div>
      </button>

    </section>
  );
};
